import React, { useState, useEffect } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import styles from './ResultTeen8.module.css';
import "./Result.css";

// Helper: map card code to image
const getCardImage = (cardString) => {
  if (!cardString) return null;
  return `/assets/img/cards/${cardString}.png`;
};

// Parse 27 cards: 8 players (3 cards each) + dealer (3 cards) = 27 total
// Order: Player 1-8 (24 cards) then Dealer (3 cards)
const parseCards = (cardString) => {
  if (!cardString) return { players: Array(8).fill([]), dealer: [] };
  const cards = cardString.split(',').map(c => c.trim());
  
  const players = [];
  for (let i = 0; i < 8; i++) {
    players.push(cards.slice(i * 3, (i + 1) * 3).map(c => getCardImage(c)));
  }
  
  const dealer = cards.slice(24, 27).map(c => getCardImage(c));
  
  return { players, dealer };
};

// Map win result to display
const mapWinCircle = (win) => {
  if (!win) return "R";
  // For teen8, results might be player numbers (1-8) or "R" for round
  if (typeof win === 'string' && win.length === 1) {
    return win;
  }
  return "R";
};

const ResultTeen8 = ({ data = [], gameData = {} }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('teen8');
        if (response?.success) {
          if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
            const mapped = response.data.data.res.map(item => ({
              mid: item.mid,
              win: mapWinCircle(item.win),
            }));
            setLastResults(mapped);
          } else if (response?.data?.res && Array.isArray(response.data.res)) {
            const mapped = response.data.res.map(item => ({
              mid: item.mid,
              win: mapWinCircle(item.win),
            }));
            setLastResults(mapped);
          }
        }
      } catch (e) {
        console.error('Error fetching last results:', e);
      }
    };
    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleResultClick = async (res, index) => {
    setSelectedResult(res);
    setLoading(true);

    let mid = null;
    if (index < lastResults.length) {
      mid = lastResults[index]?.mid;
    } else if (index < data.length + (gameData?.lrs?.length || 0)) {
      mid = gameData?.data?.data?.mid || gameData?.data?.mid || gameData?.mid;
    }

    if (mid) {
      try {
        const response = await getDetailResults('teen8', mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            setDetailResult(response.data.data.t1);
          } else if (response?.data?.t1) {
            setDetailResult(response.data.t1);
          }
        }
      } catch (e) {
        console.error('Error fetching detail results:', e);
      }
    }

    setLoading(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Combine last results with current game data results
  const allResults = lastResults.length > 0
    ? lastResults.map(r => r.win)
    : [...data, ...(gameData?.lrs || []), ...(gameData?.data?.lrs || [])].map(mapWinCircle);

  // Parse cards for modal display
  const cardData = detailResult ? parseCards(detailResult.card || detailResult.cards || "") : { players: Array(8).fill([]), dealer: [] };

  return (
    <>
      <div className={styles.resultRow}>
        {allResults.map((res, index) => (
          <div
            key={index}
            className={`${styles.resultCircle}`}
            onClick={() => handleResultClick(res, index)}
            style={{ cursor: 'pointer' }}
          >
R
          </div>
        ))}
      </div>

      {showModal && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="Teenpatti Open Result"
          roundId={detailResult?.rid || detailResult?.mid || selectedResult?.mid || ""}
          matchTime={detailResult?.match_time || ""}
          loading={loading}
        >
          {detailResult && (
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <h6 className="text-right" style={{ color: '#fff', marginBottom: '15px' }}>
                    <b>Round Id :</b> {detailResult.rid || detailResult.mid || selectedResult?.mid || ""}
                  </h6>
                </div>
              </div>
              <div className="row bottom-border-casino m-t-10">
                <div className="col-md-12">
                  {/* Row 1: Player 1, Dealer, Player 8 */}
                  <div className="row result-row">
                    {/* Player 1 */}
                    <div className="col-md-3 sixplayer-image text-center">
                      <h4 className="text-center">1</h4>
                      <div className="text-center" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                        {cardData.players[0]?.map((cardImg, cardIndex) => (
                          <img
                            key={cardIndex}
                            src={cardImg}
                            alt={`Player 1 Card ${cardIndex + 1}`}
                            style={{ width: '35px', margin: '2px',objectFit: 'contain' }}
                          />
                        ))}
                      </div>
                      <div className="text-center"></div>
                    </div>
                    {/* Dealer */}
                    {cardData.dealer.length > 0 && (
                      <div className="col-md-6 sixplayer-image text-center">
                        <h4 className="text-center">Dealer</h4>
                        <div className="text-center" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                          {cardData.dealer.map((cardImg, cardIndex) => (
                            <img
                              key={cardIndex}
                              src={cardImg}
                              alt={`Dealer Card ${cardIndex + 1}`}
                              style={{ width: '35px', margin: '2px',objectFit: 'contain' }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Player 8 */}
                    <div className="col-md-3 sixplayer-image text-center">
                      <h4 className="text-center">8</h4>
                      <div className="text-center" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                        {cardData.players[7]?.map((cardImg, cardIndex) => (
                          <img
                            key={cardIndex}
                            src={cardImg}
                            alt={`Player 8 Card ${cardIndex + 1}`}
                            style={{ width: '35px', margin: '2px',objectFit: 'contain' }}
                          />
                        ))}
                      </div>
                      <div className="text-center"></div>
                    </div>
                  </div>
                  {/* Row 2: Player 2, Empty, Player 7 */}
                  <div className="row result-row">
                    {/* Player 2 */}
                    <div className="col-md-3 sixplayer-image text-center">
                      <h4 className="text-center">2</h4>
                      <div className="text-center" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                        {cardData.players[1]?.map((cardImg, cardIndex) => (
                          <img
                            key={cardIndex}
                            src={cardImg}
                            alt={`Player 2 Card ${cardIndex + 1}`}
                            style={{ width: '35px', margin: '2px',objectFit: 'contain' }}
                          />
                        ))}
                      </div>
                      <div className="text-center"></div>
                    </div>
                    {/* Empty space */}
                    <div className="col-md-6 sixplayer-image"></div>
                    {/* Player 7 */}
                    <div className="col-md-3 sixplayer-image text-center">
                      <h4 className="text-center">7</h4>
                      <div className="text-center" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                        {cardData.players[6]?.map((cardImg, cardIndex) => (
                          <img
                            key={cardIndex}
                            src={cardImg}
                            alt={`Player 7 Card ${cardIndex + 1}`}
                            style={{ width: '35px', margin: '2px',objectFit: 'contain' }}
                          />
                        ))}
                      </div>
                      <div className="text-center"></div>
                    </div>
                  </div>
                  {/* Row 3: Player 3, Player 4, Player 5, Player 6 */}
                  <div className="row result-row">
                    {/* Player 3 */}
                    <div className="col-md-3 sixplayer-image text-center">
                      <h4 className="text-center">3</h4>
                      <div className="text-center" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                        {cardData.players[2]?.map((cardImg, cardIndex) => (
                          <img
                            key={cardIndex}
                            src={cardImg}
                            alt={`Player 3 Card ${cardIndex + 1}`}
                            style={{ width: '35px', margin: '2px',objectFit: 'contain' }}
                          />
                        ))}
                      </div>
                      <div className="text-center"></div>
                    </div>
                    {/* Player 4 */}
                    <div className="col-md-3 sixplayer-image text-center">
                      <h4 className="text-center">4</h4>
                      <div className="text-center" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                        {cardData.players[3]?.map((cardImg, cardIndex) => (
                          <img
                            key={cardIndex}
                            src={cardImg}
                            alt={`Player 4 Card ${cardIndex + 1}`}
                            style={{ width: '35px', margin: '2px',objectFit: 'contain' }}
                          />
                        ))}
                      </div>
                      <div className="text-center"></div>
                    </div>
                    {/* Player 5 */}
                    <div className="col-md-3 sixplayer-image text-center">
                      <h4 className="text-center">5</h4>
                      <div className="text-center" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                        {cardData.players[4]?.map((cardImg, cardIndex) => (
                          <img
                            key={cardIndex}
                            src={cardImg}
                            alt={`Player 5 Card ${cardIndex + 1}`}
                            style={{ width: '35px', margin: '2px',objectFit: 'contain' }}
                          />
                        ))}
                      </div>
                      <div className="text-center"></div>
                    </div>
                    {/* Player 6 */}
                    <div className="col-md-3 sixplayer-image text-center">
                      <h4 className="text-center">6</h4>
                      <div className="text-center" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                        {cardData.players[5]?.map((cardImg, cardIndex) => (
                          <img
                            key={cardIndex}
                            src={cardImg}
                            alt={`Player 6 Card ${cardIndex + 1}`}
                            style={{ width: '35px', margin: '2px',objectFit: 'contain' }}
                          />
                        ))}
                      </div>
                      <div className="text-center"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultTeen8;

